import React, { useEffect, useState } from 'react';
import api from '../services/api';
import { Card } from '../components/ui';

export default function AdminMetrics() {
  const [data, setData] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [endpoints, setEndpoints] = useState([]);
  const [users, setUsers] = useState([]);
  const [visitsDaily, setVisitsDaily] = useState([]);
  const [visitsWeekly, setVisitsWeekly] = useState([]);
  const [visitsMonthly, setVisitsMonthly] = useState([]);
  const [geo, setGeo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadData();
    // Refresh every 30 seconds
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, []);

  async function loadData() {
    setLoading(true);
    setError('');
    try {
      // Load existing metrics
      const metricsRes = await api.getMetrics();
      if (metricsRes?.item) setData(metricsRes.item);

      // Load analytics data
      try {
        const analyticsRes = await fetch('/api/analytics/summary', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('jwt')}`
          }
        });
        if (analyticsRes.ok) {
          const analyticsData = await analyticsRes.json();
          setAnalytics(analyticsData.data);
        }
      } catch (e) {
        console.error('Analytics not available:', e);
      }

      // Load endpoint stats
      try {
        const endpointsRes = await fetch('/api/analytics/endpoints?limit=10', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('jwt')}`
          }
        });
        if (endpointsRes.ok) {
          const endpointsData = await endpointsRes.json();
          setEndpoints(endpointsData.data || []);
        }
      } catch (e) {
        console.error('Endpoints not available:', e);
      }

      // Load user activity
      try {
        const usersRes = await fetch('/api/analytics/users?limit=10', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('jwt')}`
          }
        });
        if (usersRes.ok) {
          const usersData = await usersRes.json();
          setUsers(usersData.data || []);
        }
      } catch (e) {
        console.error('Users not available:', e);
      }

      // Load site visits (daily/weekly/monthly) with graceful fallbacks
      try {
        const res = await fetch('/api/analytics/visits?bucket=daily&days=30', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('jwt')}` }
        });
        if (res.ok) {
          const json = await res.json();
          setVisitsDaily(Array.isArray(json?.data) ? json.data : []);
        }
      } catch (e) { /* optional */ }
      try {
        const res = await fetch('/api/analytics/visits?bucket=weekly&weeks=12', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('jwt')}` }
        });
        if (res.ok) {
          const json = await res.json();
          setVisitsWeekly(Array.isArray(json?.data) ? json.data : []);
        }
      } catch (e) { /* optional */ }
      try {
        const res = await fetch('/api/analytics/visits?bucket=monthly&months=12', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('jwt')}` }
        });
        if (res.ok) {
          const json = await res.json();
          setVisitsMonthly(Array.isArray(json?.data) ? json.data : []);
        }
      } catch (e) { /* optional */ }

      // GEO: AI crawler hits + AI-referrer visits (30d)
      try {
        const res = await fetch('/api/analytics/geo?days=30', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('jwt')}` }
        });
        if (res.ok) {
          const json = await res.json();
          setGeo(json?.data || null);
        }
      } catch (e) { /* optional */ }
    } catch (e) {
      setError(e?.message || 'Failed to load metrics');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="container-pad" style={{ padding: 24, maxWidth: 1400, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h2 style={{ color: '#ff7a45', margin: 0 }}>Admin Metrics & Analytics</h2>
        <button 
          onClick={loadData}
          style={{
            padding: '8px 16px',
            background: '#ff5a1f',
            color: '#fff',
            border: 'none',
            borderRadius: 8,
            cursor: 'pointer',
            fontSize: 14
          }}
        >
          🔄 Refresh
        </button>
      </div>
      {loading && !data ? (
        <p>Loading…</p>
      ) : error ? (
        <p style={{ color: '#fca5a5' }}>{error}</p>
      ) : (
        <>
          {/* Analytics Summary Cards */}
          {analytics && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16, marginBottom: 24 }}>
              <Card>
                <div style={{ fontSize: 14, color: '#9ca3af', marginBottom: 8 }}>Total Requests</div>
                <div style={{ fontSize: 32, fontWeight: 'bold', color: '#e5e7eb' }}>
                  {analytics.totalRequests?.toLocaleString() || 0}
                </div>
                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                  {analytics.recentRequests || 0} in last 100
                </div>
              </Card>
              <Card>
                <div style={{ fontSize: 14, color: '#9ca3af', marginBottom: 8 }}>Success Rate</div>
                <div style={{ fontSize: 32, fontWeight: 'bold', color: '#10b981' }}>
                  {analytics.successRate?.toFixed(1) || 0}%
                </div>
                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                  Last 100 requests
                </div>
              </Card>
              <Card>
                <div style={{ fontSize: 14, color: '#9ca3af', marginBottom: 8 }}>Avg Response Time</div>
                <div style={{ fontSize: 32, fontWeight: 'bold', color: '#60a5fa' }}>
                  {analytics.avgResponseTime || 0}ms
                </div>
                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                  Server performance
                </div>
              </Card>
              <Card>
                <div style={{ fontSize: 14, color: '#9ca3af', marginBottom: 8 }}>Active Users</div>
                <div style={{ fontSize: 32, fontWeight: 'bold', color: '#ff7a45' }}>
                  {analytics.activeUsers?.length || 0}
                </div>
                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                  Recent activity
                </div>
              </Card>
            </div>
          )}

          {/* Site Visits (always visible with empty states) */}
          <div className="grid-auto" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16, marginBottom: 24 }}>
              <Card title="📈 Site Visits (Daily - last 30 days)">
                {visitsDaily.length > 0 ? (
                  <div>
                    <div className="table-scroll">
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid #374151' }}>
                          <th style={{ textAlign: 'left', padding: '8px 4px', color: '#9ca3af', fontSize: 12 }}>Date</th>
                          <th style={{ textAlign: 'right', padding: '8px 4px', color: '#9ca3af', fontSize: 12 }}>Visits</th>
                        </tr>
                      </thead>
                      <tbody>
                        {visitsDaily.map((d) => (
                          <tr key={`vd_${d.bucket}`} style={{ borderBottom: '1px solid #1f2937' }}>
                            <td style={{ padding: '6px 4px', color: '#e5e7eb' }}>{d.bucket}</td>
                            <td style={{ padding: '6px 4px', color: '#e5e7eb', textAlign: 'right' }}>{(d.count||0).toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    </div>
                    {(() => {
                      const max = Math.max(...visitsDaily.map(v => Number(v.count)||0), 1);
                      return (
                        <div style={{ marginTop: 10, display: 'grid', gap: 6 }}>
                          {visitsDaily.map((v) => (
                            <div key={`vd_bar_${v.bucket}`} style={{ display: 'grid', gridTemplateColumns: '100px 1fr', gap: 8, alignItems: 'center' }}>
                              <div style={{ color: '#9ca3af', fontSize: 12 }}>{v.bucket}</div>
                              <div style={{ background: '#0b0b0e', borderRadius: 999, overflow: 'hidden' }}>
                                <div style={{ width: `${(Number(v.count)||0)/max*100}%`, height: 8, background: '#ff7a45' }} />
                              </div>
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                ) : (
                  <div style={{ color: '#9ca3af' }}>No daily visit data</div>
                )}
              </Card>

              <Card title="📈 Site Visits (Weekly - last 12 weeks)">
                {visitsWeekly.length > 0 ? (
                  <div>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid #374151' }}>
                          <th style={{ textAlign: 'left', padding: '8px 4px', color: '#9ca3af', fontSize: 12 }}>Week</th>
                          <th style={{ textAlign: 'right', padding: '8px 4px', color: '#9ca3af', fontSize: 12 }}>Visits</th>
                        </tr>
                      </thead>
                      <tbody>
                        {visitsWeekly.map((d) => (
                          <tr key={`vw_${d.bucket}`} style={{ borderBottom: '1px solid #1f2937' }}>
                            <td style={{ padding: '6px 4px', color: '#e5e7eb' }}>{d.bucket}</td>
                            <td style={{ padding: '6px 4px', color: '#e5e7eb', textAlign: 'right' }}>{(d.count||0).toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {(() => {
                      const max = Math.max(...visitsWeekly.map(v => Number(v.count)||0), 1);
                      return (
                        <div style={{ marginTop: 10, display: 'grid', gap: 6 }}>
                          {visitsWeekly.map((v) => (
                            <div key={`vw_bar_${v.bucket}`} style={{ display: 'grid', gridTemplateColumns: '100px 1fr', gap: 8, alignItems: 'center' }}>
                              <div style={{ color: '#9ca3af', fontSize: 12 }}>{v.bucket}</div>
                              <div style={{ background: '#0b0b0e', borderRadius: 999, overflow: 'hidden' }}>
                                <div style={{ width: `${(Number(v.count)||0)/max*100}%`, height: 8, background: '#34d399' }} />
                              </div>
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                ) : (
                  <div style={{ color: '#9ca3af' }}>No weekly visit data</div>
                )}
              </Card>

              <Card title="📈 Site Visits (Monthly - last 12 months)">
                {visitsMonthly.length > 0 ? (
                  <div>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid #374151' }}>
                          <th style={{ textAlign: 'left', padding: '8px 4px', color: '#9ca3af', fontSize: 12 }}>Month</th>
                          <th style={{ textAlign: 'right', padding: '8px 4px', color: '#9ca3af', fontSize: 12 }}>Visits</th>
                        </tr>
                      </thead>
                      <tbody>
                        {visitsMonthly.map((d) => (
                          <tr key={`vm_${d.bucket}`} style={{ borderBottom: '1px solid #1f2937' }}>
                            <td style={{ padding: '6px 4px', color: '#e5e7eb' }}>{d.bucket}</td>
                            <td style={{ padding: '6px 4px', color: '#e5e7eb', textAlign: 'right' }}>{(d.count||0).toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {(() => {
                      const max = Math.max(...visitsMonthly.map(v => Number(v.count)||0), 1);
                      return (
                        <div style={{ marginTop: 10, display: 'grid', gap: 6 }}>
                          {visitsMonthly.map((v) => (
                            <div key={`vm_bar_${v.bucket}`} style={{ display: 'grid', gridTemplateColumns: '100px 1fr', gap: 8, alignItems: 'center' }}>
                              <div style={{ color: '#9ca3af', fontSize: 12 }}>{v.bucket}</div>
                              <div style={{ background: '#0b0b0e', borderRadius: 999, overflow: 'hidden' }}>
                                <div style={{ width: `${(Number(v.count)||0)/max*100}%`, height: 8, background: '#60a5fa' }} />
                              </div>
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                ) : (
                  <div style={{ color: '#9ca3af' }}>No monthly visit data</div>
                )}
              </Card>
          </div>

          {/* GEO: AI crawlers + AI referrers (last 30 days) */}
          {geo && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16, marginBottom: 24 }}>
              <Card title="🤖 AI Crawler Activity (30d)">
                <div style={{ fontSize: 28, fontWeight: 'bold', color: '#e5e7eb', marginBottom: 8 }}>
                  {geo.crawlers?.total?.toLocaleString() || 0} fetches
                </div>
                {geo.crawlers?.bots?.length > 0 ? (
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <tbody>
                      {geo.crawlers.bots.slice(0, 10).map((b) => (
                        <tr key={b.bot} style={{ borderBottom: '1px solid #1f2937' }}>
                          <td style={{ padding: '4px 4px', color: '#e5e7eb', fontSize: 13 }}>{b.bot}</td>
                          <td style={{ padding: '4px 4px', color: '#9ca3af', fontSize: 13, textAlign: 'right' }}>{b.hits}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div style={{ color: '#9ca3af' }}>No AI crawler hits yet — crawl precedes citation.</div>
                )}
                {geo.crawlers?.topPaths?.length > 0 && (
                  <div style={{ marginTop: 10, fontSize: 12, color: '#6b7280' }}>
                    Top paths: {geo.crawlers.topPaths.slice(0, 5).map((p) => `${p.path} (${p.hits})`).join(' · ')}
                  </div>
                )}
              </Card>
              <Card title="🧠 AI Referrers (30d)">
                <div style={{ fontSize: 28, fontWeight: 'bold', color: '#e5e7eb', marginBottom: 8 }}>
                  {geo.aiReferrers?.views || 0} views · {geo.aiReferrers?.uniques || 0} uniques
                </div>
                {geo.aiReferrers?.hosts?.length > 0 ? (
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <tbody>
                      {geo.aiReferrers.hosts.map((h) => (
                        <tr key={h.host} style={{ borderBottom: '1px solid #1f2937' }}>
                          <td style={{ padding: '4px 4px', color: '#e5e7eb', fontSize: 13 }}>{h.host}</td>
                          <td style={{ padding: '4px 4px', color: '#9ca3af', fontSize: 13, textAlign: 'right' }}>{h.views}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div style={{ color: '#9ca3af' }}>No visits from ChatGPT/Perplexity/Claude yet.</div>
                )}
              </Card>
            </div>
          )}

          {/* Top Endpoints */}
          {endpoints.length > 0 && (
            <Card title="🔥 Top API Endpoints (Last 24 Hours)" style={{ marginBottom: 24 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #374151' }}>
                    <th style={{ textAlign: 'left', padding: '8px 4px', color: '#9ca3af', fontSize: 12 }}>Endpoint</th>
                    <th style={{ textAlign: 'right', padding: '8px 4px', color: '#9ca3af', fontSize: 12 }}>Requests</th>
                    <th style={{ textAlign: 'right', padding: '8px 4px', color: '#9ca3af', fontSize: 12 }}>Avg Time</th>
                    <th style={{ textAlign: 'right', padding: '8px 4px', color: '#9ca3af', fontSize: 12 }}>Errors</th>
                  </tr>
                </thead>
                <tbody>
                  {endpoints.map((ep, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #1f2937' }}>
                      <td style={{ padding: '8px 4px', color: '#e5e7eb', fontSize: 14 }}>
                        <span style={{ 
                          padding: '2px 6px', 
                          background: '#374151', 
                          borderRadius: 4, 
                          fontSize: 12,
                          marginRight: 8
                        }}>
                          {ep.method}
                        </span>
                        {ep.path}
                      </td>
                      <td style={{ padding: '8px 4px', color: '#e5e7eb', textAlign: 'right' }}>
                        {ep.totalRequests?.toLocaleString()}
                      </td>
                      <td style={{ padding: '8px 4px', color: '#60a5fa', textAlign: 'right' }}>
                        {ep.avgDuration}ms
                      </td>
                      <td style={{ padding: '8px 4px', color: ep.errors > 0 ? '#f87171' : '#6b7280', textAlign: 'right' }}>
                        {ep.errors} ({ep.errorRate?.toFixed(1)}%)
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}

          {/* Active Users */}
          {users.length > 0 && (
            <Card title="👥 Active Users (Last 7 Days)" style={{ marginBottom: 24 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #374151' }}>
                    <th style={{ textAlign: 'left', padding: '8px 4px', color: '#9ca3af', fontSize: 12 }}>Email</th>
                    <th style={{ textAlign: 'right', padding: '8px 4px', color: '#9ca3af', fontSize: 12 }}>Requests</th>
                    <th style={{ textAlign: 'right', padding: '8px 4px', color: '#9ca3af', fontSize: 12 }}>Errors</th>
                    <th style={{ textAlign: 'right', padding: '8px 4px', color: '#9ca3af', fontSize: 12 }}>Last Seen</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #1f2937' }}>
                      <td style={{ padding: '8px 4px', color: '#e5e7eb', fontSize: 14 }}>
                        {user.email || 'Anonymous'}
                      </td>
                      <td style={{ padding: '8px 4px', color: '#e5e7eb', textAlign: 'right' }}>
                        {user.totalRequests?.toLocaleString()}
                      </td>
                      <td style={{ padding: '8px 4px', color: user.errors > 0 ? '#f87171' : '#6b7280', textAlign: 'right' }}>
                        {user.errors} ({user.errorRate?.toFixed(1)}%)
                      </td>
                      <td style={{ padding: '8px 4px', color: '#9ca3af', textAlign: 'right', fontSize: 12 }}>
                        {new Date(user.lastSeen).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}

          {/* Existing Metrics */}
          <section style={{ marginBottom: 16, background: '#111827', border: '1px solid #374151', borderRadius: 12, padding: 16 }}>
            <h3 style={{ marginTop: 0, color: '#e5e7eb' }}>Totals</h3>
            <ul>
              <li>Uploads: {data.uploads?.total ?? 0}</li>
              <li>Ads: {data.ads?.total ?? 0}</li>
              <li>Streams: {data.streams?.total ?? 0}</li>
              <li>Transcripts: {data.transcripts?.total ?? 0}</li>
              <li>Now: {data.system?.now || '-'}</li>
              <li>Uptime (sec): {data.system?.uptimeSec ?? 0}</li>
              <li>RSS (MB): {data.system?.rssMb ?? 0}</li>
            </ul>
          </section>
          <section style={{ background: '#111827', border: '1px solid #374151', borderRadius: 12, padding: 16 }}>
            <h3 style={{ marginTop: 0, color: '#e5e7eb' }}>Uploads (Last 7 Days)</h3>
            {Array.isArray(data.uploads?.last7Days) && data.uploads.last7Days.length ? (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', borderBottom: '1px solid #374151', padding: '8px 4px' }}>Date</th>
                    <th style={{ textAlign: 'left', borderBottom: '1px solid #374151', padding: '8px 4px' }}>Count</th>
                  </tr>
                </thead>
                <tbody>
                  {data.uploads.last7Days.map((r) => (
                    <tr key={r.date}>
                      <td style={{ padding: '6px 4px', borderBottom: '1px solid #111827' }}>{r.date}</td>
                      <td style={{ padding: '6px 4px', borderBottom: '1px solid #111827' }}>{r.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div style={{ color: '#9ca3af' }}>No recent uploads</div>
            )}
            {Array.isArray(data.uploads?.last7Days) && data.uploads.last7Days.length > 0 && (
              <div style={{ marginTop: 10 }}>
                <div style={{ color: '#9ca3af', marginBottom: 6, fontSize: 12 }}>Uploads trend</div>
                <div style={{ display: 'grid', gap: 6 }}>
                  {(() => {
                    const max = Math.max(...data.uploads.last7Days.map(d => Number(d.count)||0), 1);
                    return data.uploads.last7Days.map((d) => (
                      <div key={`u_${d.date}`} style={{ display: 'grid', gridTemplateColumns: '120px 1fr', alignItems: 'center', gap: 8 }}>
                        <div style={{ color: '#9ca3af', fontSize: 12 }}>{d.date}</div>
                        <div style={{ background: '#0b0b0e', borderRadius: 999, overflow: 'hidden' }}>
                          <div style={{ width: `${(Number(d.count)||0) / max * 100}%`, height: 8, background: '#ff5a1f' }} />
                        </div>
                      </div>
                    ));
                  })()}
                </div>
              </div>
            )}
          </section>

          <section style={{ marginTop: 16, background: '#111827', border: '1px solid #374151', borderRadius: 12, padding: 16 }}>
            <h3 style={{ marginTop: 0, color: '#e5e7eb' }}>Ads (Last 7 Days)</h3>
            {Array.isArray(data.ads?.last7Days) && data.ads.last7Days.length ? (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', borderBottom: '1px solid #374151', padding: '8px 4px' }}>Date</th>
                    <th style={{ textAlign: 'left', borderBottom: '1px solid #374151', padding: '8px 4px' }}>Count</th>
                  </tr>
                </thead>
                <tbody>
                  {data.ads.last7Days.map((r) => (
                    <tr key={r.date}>
                      <td style={{ padding: '6px 4px', borderBottom: '1px solid #111827' }}>{r.date}</td>
                      <td style={{ padding: '6px 4px', borderBottom: '1px solid #111827' }}>{r.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div style={{ color: '#9ca3af' }}>No recent ads</div>
            )}
            {Array.isArray(data.ads?.last7Days) && data.ads.last7Days.length > 0 && (
              <div style={{ marginTop: 10 }}>
                <div style={{ color: '#9ca3af', marginBottom: 6, fontSize: 12 }}>Ads trend</div>
                <div style={{ display: 'grid', gap: 6 }}>
                  {(() => {
                    const max = Math.max(...data.ads.last7Days.map(d => Number(d.count)||0), 1);
                    return data.ads.last7Days.map((d) => (
                      <div key={`a_${d.date}`} style={{ display: 'grid', gridTemplateColumns: '120px 1fr', alignItems: 'center', gap: 8 }}>
                        <div style={{ color: '#9ca3af', fontSize: 12 }}>{d.date}</div>
                        <div style={{ background: '#0b0b0e', borderRadius: 999, overflow: 'hidden' }}>
                          <div style={{ width: `${(Number(d.count)||0) / max * 100}%`, height: 8, background: '#34d399' }} />
                        </div>
                      </div>
                    ));
                  })()}
                </div>
              </div>
            )}
          </section>

          <section style={{ marginTop: 16, background: '#111827', border: '1px solid #374151', borderRadius: 12, padding: 16 }}>
            <h3 style={{ marginTop: 0, color: '#e5e7eb' }}>Streams (Last 7 Days)</h3>
            {Array.isArray(data.streams?.last7Days) && data.streams.last7Days.length ? (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', borderBottom: '1px solid #374151', padding: '8px 4px' }}>Date</th>
                    <th style={{ textAlign: 'left', borderBottom: '1px solid #374151', padding: '8px 4px' }}>Count</th>
                  </tr>
                </thead>
                <tbody>
                  {data.streams.last7Days.map((r) => (
                    <tr key={r.date}>
                      <td style={{ padding: '6px 4px', borderBottom: '1px solid #111827' }}>{r.date}</td>
                      <td style={{ padding: '6px 4px', borderBottom: '1px solid #111827' }}>{r.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div style={{ color: '#9ca3af' }}>No recent streams</div>
            )}
            {Array.isArray(data.streams?.last7Days) && data.streams.last7Days.length > 0 && (
              <div style={{ marginTop: 10 }}>
                <div style={{ color: '#9ca3af', marginBottom: 6, fontSize: 12 }}>Streams trend</div>
                <div style={{ display: 'grid', gap: 6 }}>
                  {(() => {
                    const max = Math.max(...data.streams.last7Days.map(d => Number(d.count)||0), 1);
                    return data.streams.last7Days.map((d) => (
                      <div key={`s_${d.date}`} style={{ display: 'grid', gridTemplateColumns: '120px 1fr', alignItems: 'center', gap: 8 }}>
                        <div style={{ color: '#9ca3af', fontSize: 12 }}>{d.date}</div>
                        <div style={{ background: '#0b0b0e', borderRadius: 999, overflow: 'hidden' }}>
                          <div style={{ width: `${(Number(d.count)||0) / max * 100}%`, height: 8, background: '#60a5fa' }} />
                        </div>
                      </div>
                    ));
                  })()}
                </div>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}
